import { ref, watch, computed } from 'vue'
import { defineStore } from 'pinia'
import { useStorage } from 'vue3-storage'
import { fetchProducts, fetchProductsBySku } from '@/api/product'


const storage = useStorage()

export const useProductStore = defineStore("product", () =>{
    const products = ref([])
    const cache_timeout = 60 * 5 * 1000;
    const fetch_delay = 200;

    const toFetchByProdcutId = ref<{ id: any; resolve: (value: unknown) => void; reject: (reason?: any) => void; }[]>([]);
    const toFetchByArticleId = ref<{ id: any; resolve: (value: unknown) => void; reject: (reason?: any) => void; }[]>([]);

    // we want to watch the toFetchByProdcutId and toFetchByArticleId with a delay of x milliseconds
    watch(toFetchByProdcutId, async (toFetch) => {
        // we build the list of ids to fetch making sure we don't have duplicates
        await new Promise((resolve) => setTimeout(resolve, fetch_delay))
        const ids = Array.from(new Set(toFetch.map(({id}) => id)))
        if(ids.length === 0){
            return
        }
        fetchProducts(ids)
            .then((response) => {
                const entry_to_delete = []
                for( const prod in response){
                    // we add the last_fetch property to the product
                    response[prod].last_fetch = Date.now()
                    // we update the product or add it to the list
                    const index = products.value.findIndex(product => product.id === response[prod].id)
                    if(index === -1){
                        products.value.push(response[prod])
                    }else{
                        products.value[index] = response[prod]
                    }
                    // we search for the products in the toFetch list
                    const entries = toFetch.filter(entry => entry.id === response[prod].id)
                    // if prod.error exists we reject the promise
                    if(response[prod].error){
                        entries.forEach(entry => entry.reject())
                    }
                    else{
                        entries.forEach(entry => entry.resolve(response[prod]))
                    }
                    // we add the entries to delete
                    entry_to_delete.push(...entries)
                }
                // we remove the entries from the toFetch list
                toFetchByProdcutId.value = toFetchByProdcutId.value.filter(entry => !entry_to_delete.includes(entry))
            })
            .catch((error) => {
                // if it's a 504 we relaunch it
                console.log(error);
                if(error.code=="ERR_NETWORK" || (error.response && error.response.status === 504)){
                    toFetchByProdcutId.value.push(...toFetch)
                }
                else{
                    // we should send back with N/A information
                    const entry_to_delete = []
                    ids.forEach(id => {
                        const entries = toFetch.filter(entry => entry.id === id)
                        entries.forEach(entry => entry.reject())
                        entry_to_delete.push(...entries)
                    })
                    toFetchByProdcutId.value = toFetchByProdcutId.value.filter(entry => !entry_to_delete.includes(entry))
                }
            })
    }, { deep: true })
    watch(toFetchByArticleId, async (toFetch) => {
        // we build the list of ids to fetch making sure we don't have duplicates
        await new Promise((resolve) => setTimeout(resolve, fetch_delay))
        const ids = Array.from(new Set(toFetch.map(({id}) => id)))
        if(ids.length === 0){
            return
        }
        fetchProductsBySku(ids)
            .then((response) => {
                const entry_to_delete = []
                for( const prod in response){
                    // we add the last_fetch property to the product
                    response[prod].last_fetch = Date.now()
                    // we update the product or add it to the list
                    const index = products.value.findIndex(product => product.id === response[prod].id )
                    if(index === -1){
                        products.value.push(response[prod])
                    }else{
                        products.value[index] = response[prod]
                    }
                    if(response[prod].error){
                        const entries = toFetch.filter(entry => entry.id === response[prod].id)
                        entries.forEach(entry => entry.reject())
                        entry_to_delete.push(...entries)
                    }
                    else{
                        // we search for the products in the toFetch list where entry.id === response[prod].articles[...].id 
                        const entries = toFetch.filter(entry => response[prod].articles.find(article => article.sku === entry.id || article.id == entry.id))
                        // we resolve the promises
                        if(response[prod].error){
                            entries.forEach(entry => entry.reject())
                        }
                        else{
                            entries.forEach(entry => entry.resolve(response[prod]))
                        }
                        // we add the entries to delete
                        entry_to_delete.push(...entries)
                    }
                }
                // we remove the entries from the toFetch list
                toFetchByArticleId.value = toFetchByArticleId.value.filter(entry => !entry_to_delete.includes(entry))
            })
            .catch((error) => {
                // if it's a 504 we relaunch it
                console.log(error);
                if(error.code=="ERR_NETWORK" || (error.response && error.response.status === 504)){
                    toFetchByArticleId.value.push(...toFetch)
                }
                else{
                    // we should send back with N/A information
                    const entry_to_delete = []
                    ids.forEach(id => {
                        const entries = toFetch.filter(entry => entry.id === id)
                        entries.forEach(entry => entry.reject())
                        entry_to_delete.push(...entries)
                    })
                    toFetchByArticleId.value = toFetchByArticleId.value.filter(entry => !entry_to_delete.includes(entry))
                }
            })
    }, { deep: true })

    function getProductByArticleId(id){
        return new Promise((resolve, reject) => {
            try{
                const prod = products.value.find(product => product.articles.find(article => article.id === id))
                if(prod){
                    if(prod.last_fetch + cache_timeout < Date.now() || prod.reload){
                        toFetchByArticleId.value.push({id, resolve: () => {}, reject: () => {}});
                    }
                    resolve(prod)
                }else{
                    toFetchByArticleId.value.push({id, resolve, reject})
                }
            }catch(e){
                toFetchByArticleId.value.push({id, resolve, reject})
                console.log(e)
            }
        })
    }
    function getProductById(id){
        return new Promise((resolve, reject) => {
            const prod = products.value.find(product => product.id === id)
            if(prod){
                if(prod.last_fetch + cache_timeout < Date.now() || prod.reload){
                    toFetchByProdcutId.value.push({id, resolve: () => {}, reject: () => {}});
                }
                resolve(prod)
            }else{
                console.log("pushing to fetch")
                toFetchByProdcutId.value.push({id, resolve, reject})
            }
        })
    }
    function getProductBySku(sku){
        return new Promise((resolve, reject) => {
            // sku can be find in products.articles.sku
            if(products.value === undefined || !products.value || products.value.length === 0){
                toFetchByArticleId.value.push({id: sku, resolve, reject})
            }
            else{
                const prod = products.value.find(product => product.articles?.find(article => article.sku === sku))
                if(prod){
                    if(prod.last_fetch + cache_timeout < Date.now() || prod.reload){
                        toFetchByArticleId.value.push({id: sku, resolve: () => {}, reject: () => {}});
                    }
                    resolve(prod)
                }else{
                    toFetchByArticleId.value.push({id: sku, resolve, reject})
                }
            }
        })
    }
    return {
        products, getProductById, getProductBySku, getProductByArticleId, toFetchByProdcutId
    }
})